import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

// Células no mesmo estilo da TabelaAnalise
const CenteredCell = styled(TableCell)(() => ({
  textAlign: "center",
  fontWeight: 500,
  whiteSpace: "nowrap",
}));

const ConditionalCell = styled(CenteredCell)(({ theme, valor }) => ({
  color:
    valor > 0
      ? "#FF0000" // acima do remunerado: vermelho
      : valor < 0
      ? "#00B159" // abaixo do remunerado: verde
      : theme.palette.text.primary,
}));

const N = (v) => {
  if (v == null || v === "") return 0;

  // Trata números no formato brasileiro: "1.280,00"
  if (typeof v === "string") {
    const cleaned = v.replace(/\./g, "").replace(",", ".");
    const n = Number(cleaned);
    return Number.isNaN(n) ? 0 : n;
  }

  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
};

const fmtBRL = (v) =>
  N(v).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtPct = (v) => `${N(v).toFixed(2).replace(".", ",")}%`;

/**
 * Tabela de detalhamento por DESCRIÇÃO (Insumos)
 *
 * Fonte de dados:
 *  - rows: linhas da VW_BSP_COMPRACONSUMO (já filtradas por período),
 *          contendo pelo menos: GR_ANALISE, GR_COMPRA, DESCRICAO, VALOR_COMPRA.
 *  - immuGrupo: IMMU de referência para o par (GR_ANALISE, GR_COMPRA), vindo da BSP_PREVISAOCOMPRA.
 *
 * A lógica é:
 *  - filtra rows pelo selectedGroup (GR_ANALISE) e selectedCompra (GR_COMPRA);
 *  - agrupa por DESCRICAO;
 *  - soma VALOR_COMPRA -> GASTO_REAL de cada descrição;
 *  - calcula CUSTO_REMUNERADO_IMMU do insumo: immuGrupo * kmAcopTotal;
 *  - reparte esse custo entre as descrições de forma proporcional ao GASTO_REAL;
 *  - calcula DIFERENÇA, DIFERENÇA %, CUSTO IMMU e CUSTO EMPRESA por descrição.
 */
export default function Tab_Insumo_Compra({
  rows = [],
  selectedGroup,
  selectedCompra,
  kmAcopTotal = 0,
  immuGrupo = 0,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const {
    linhas,
    totalRealGrupo,
    custoRemuGrupo,
    hasSelection,
  } = useMemo(() => {
    // se não houver grupo/insumo selecionado, nem tenta filtrar
    if (!selectedGroup || !selectedCompra) {
      return {
        linhas: [],
        totalRealGrupo: 0,
        custoRemuGrupo: 0,
        hasSelection: false,
      };
    }

    if (!rows || !rows.length) {
      return {
        linhas: [],
        totalRealGrupo: 0,
        custoRemuGrupo: 0,
        hasSelection: true,
      };
    }

    // comparamos pelo código numérico (ex.: "002", "018") para evitar problemas
    // de espaços/acentos na descrição do grupo/compra.
    const extractCode = (s) => {
      const str = String(s || "").trim();
      const m = str.match(/^(\d{1,4})/); // pega os dígitos iniciais (001, 002, 018, 034, etc.)
      return m ? m[1] : str;
    };

    const sgCode = extractCode(selectedGroup);
    const scCode = extractCode(selectedCompra);

    // Filtra pelos parâmetros atuais
    const filtradas = rows.filter((r) => {
      const grACode = extractCode(r.GR_ANALISE);
      const grCCode = extractCode(r.GR_COMPRA);
      return grACode === sgCode && grCCode === scCode;
    });

    const byDesc = new Map();
    let totalRealGrupo = 0;

    for (const r of filtradas) {
      const desc = String(r.DESCRICAO || "—").trim() || "—";

      // Gasto Real = soma de VALOR_COMPRA da VW_BSP_COMPRACONSUMO
      const rawReal = r.VALOR_COMPRA ?? r.VALOR ?? 0;
      const real = N(rawReal);

      totalRealGrupo += real;

      if (!byDesc.has(desc)) {
        byDesc.set(desc, {
          descricao: desc,
          gastoReal: 0,
        });
      }
      const acc = byDesc.get(desc);
      acc.gastoReal += real;
    }

    const kmTotal = N(kmAcopTotal);
    const custoRemuGrupo =
      kmTotal > 0 ? N(immuGrupo) * kmTotal : 0;

    // Constrói linhas finais, repartindo custo remunerado proporcionalmente ao Gasto Real
    let linhas = Array.from(byDesc.values()).map((item) => {
      const share =
        totalRealGrupo > 0 ? item.gastoReal / totalRealGrupo : 0;

      const custoRemu = custoRemuGrupo * share;

      // ainda não temos META por descrição; mantemos TETO DE GASTO = 0
      const tetoGasto = 0;

      const diferenca = item.gastoReal - custoRemu;
      const difPct =
        custoRemu > 0 ? (diferenca / custoRemu) * 100 : 0;

      const custoImmu =
        kmTotal > 0 ? custoRemu / kmTotal : 0;

      const custoEmpresa =
        kmTotal > 0 ? item.gastoReal / kmTotal : 0;

      return {
        descricao: item.descricao,
        gastoReal: item.gastoReal,
        custoRemu,
        tetoGasto,
        diferenca,
        difPct,
        custoImmu,
        custoEmpresa,
      };
    });

    // mantém apenas descrições que tiveram compras (Gasto Real > 0)
    linhas = linhas.filter((ln) => ln.gastoReal > 0);

    // Ordena por maior Gasto Real
    linhas.sort((a, b) => b.gastoReal - a.gastoReal);

    return { linhas, totalRealGrupo, custoRemuGrupo, hasSelection: true };
  }, [rows, selectedGroup, selectedCompra, kmAcopTotal, immuGrupo]);

  // Se não tiver grupo/insumo selecionado, não renderiza nada
  if (!hasSelection) {
    return null;
  }

  return (
    <Box mt={3} p={2} border="2px solid #0ea5e9" borderRadius={4}>
      <Typography
        variant="h6"
        align="center"
        sx={{ fontWeight: "bold", mb: 1 }}
      >
        DETALHAMENTO DE INSUMOS
      </Typography>

      <Typography
        variant="subtitle2"
        align="center"
        sx={{ mb: 2, opacity: 0.8 }}
      >
        {selectedGroup} — {selectedCompra}
      </Typography>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: 4,
          maxHeight: 400,      // altura máxima ~ 400px
          overflowY: "auto",   // barra de rolagem vertical
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: isDark ? "#2B3242" : "#f5f5f5",
                "& .MuiTableCell-root": {
                  fontWeight: "bold",
                },
              }}
            >
              <TableCell>DESCRIÇÃO</TableCell>
              <CenteredCell>CUSTO REMUNERADO IMMU</CenteredCell>
              <CenteredCell>TETO DE GASTO</CenteredCell>
              <CenteredCell>GASTO REAL</CenteredCell>
              <CenteredCell>DIFERENÇA</CenteredCell>
              <CenteredCell>DIFERENÇA (%)</CenteredCell>
              <CenteredCell>CUSTO IMMU</CenteredCell>
              <CenteredCell>CUSTO EMPRESA</CenteredCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {linhas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Não há descrições (insumos detalhados) para este
                    grupo/insumo no período selecionado.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              linhas.map((ln) => (
                <TableRow
                  key={ln.descricao}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: theme.palette.action.hover,
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {ln.descricao}
                  </TableCell>
                  <CenteredCell>{fmtBRL(ln.custoRemu)}</CenteredCell>
                  <CenteredCell>{fmtBRL(ln.tetoGasto)}</CenteredCell>
                  <CenteredCell>{fmtBRL(ln.gastoReal)}</CenteredCell>
                  <ConditionalCell valor={ln.diferenca}>
                    {fmtBRL(ln.diferenca)}
                  </ConditionalCell>
                  <ConditionalCell valor={ln.diferenca}>
                    {fmtPct(ln.difPct)}
                  </ConditionalCell>
                  <CenteredCell>{fmtBRL(ln.custoImmu)}</CenteredCell>
                  <CenteredCell>{fmtBRL(ln.custoEmpresa)}</CenteredCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={1} display="flex" justifyContent="space-between">
        <Typography variant="caption">
          Gasto Real do insumo (total):{" "}
          <strong>{fmtBRL(totalRealGrupo)}</strong>
        </Typography>
        <Typography variant="caption">
          Custo Remunerado IMMU (total insumo):{" "}
          <strong>{fmtBRL(custoRemuGrupo)}</strong>
        </Typography>
      </Box>
    </Box>
  );
}
